import MetaProperties from "../../../../Global/Models/Pagination/MetaProperties";
import MiniGamesContext from "../MiniGamesContext";
import MiniGamesContextTag from "../MiniGamesContextTag";
import BaseRemapper from "../../../../Global/Remapper/BaseRemapper";
import RelatedEntityRelationship from "../../../../Global/Models/Related/RelatedEntityRelationship";
import PersonalityQuizParticipationBodyModel from "../PersonalityQuiz/Participation/PersonalityQuizParticipationBodyModel";
import PersonalityQuizzesModel from "../PersonalityQuiz/PersonalityQuizzesModel";
import PersonalityQuizFullModel from "../PersonalityQuiz/PersonalityQuizFullModel";
import PersonalityQuizPersonaResultsModel from "../PersonalityQuiz/PersonalityQuizPersonaResultsModel";
import PersonalityQuizQuestionModel from "../PersonalityQuiz/PersonalityQuizQuestionModel";
import PersonalityQuizOptionModel from "../PersonalityQuiz/PersonalityQuizOptionModel";
import PreferenceMappingModel from "../../../../Global/Models/PreferenceMapping/PreferenceMappingModel";
import PersonalityQuizPersonaPointsModel from "../PersonalityQuiz/PersonalityQuizPersonaPointsModel";
import PersonalityQuizBasicModel from "../PersonalityQuiz/PersonalityQuizBasicModel";
import PersonalityQuizParticipationModel from "../PersonalityQuiz/Participation/PersonalityQuizParticipationModel";
import PersonalityQuizParticipationQuestionModel from "../PersonalityQuiz/Participation/PersonalityQuizParticipationQuestionModel";
import PersonalityQuizPersonaPercentsModel from "../PersonalityQuiz/Participation/PersonalityQuizPersonaPercentsModel";
import PersonalityQuizUserParticipationsModel from "../PersonalityQuiz/Participation/PersonalityQuizUserParticipationsModel";

export default class PersonalityQuizRemapper extends BaseRemapper {
  public remapPersonalityQuizzes = (response: any): PersonalityQuizzesModel => {
    response.meta.pagination = this.remapMetaPagination(
      response.meta.pagination
    );
    response.data = response.data.map((value) =>
      this.remapBasicPersonalityQuiz(value)
    );

    return response;
  };

  public remapPersonalityQuizById = (
    response: any
  ): PersonalityQuizFullModel => {
    const model = new PersonalityQuizFullModel();

    model.id = response.id;
    model.title = response.title;
    model.alternativeTitle = response.alternative_title;
    model.description = response.description;
    model.images = response.images;
    model.participationCount = response.participation_count;
    model.status = response.status;
    model.language = response.language;
    model.authRequirement = response.auth_requirement;
    model.flags = response.flags;
    model.adContent = response.ad_content;
    model.preferencesEnabled = response.preferences_enabled || false;
    model.rules = response.rules || null;
    model.customFields = response.custom_fields;
    model.branding = response.branding
      ? this.remapBranding(response.branding)
      : null;
    model.labels = response.labels;
    model.time = response.time;
    model.related = response.related ? this.remapRelated(response.related) : [];
    model.maxAttempts = response.max_attempts;
    model.context = response.context
      ? this.remapContext(response.context)
      : response.context;
    model.questions = response.questions
      ? response.questions.map((value) => this.remapQuestions(value))
      : [];
    model.personaResults = response.persona_results
      ? response.persona_results.map((value) => this.remapPersonaResults(value))
      : [];
    model.createdAt = response.created_at;
    model.updatedAt = response.updated_at;

    return model;
  };

  public remapPersonalityQuizParticipation = (
    participation: any
  ): PersonalityQuizParticipationModel => {
    const model = new PersonalityQuizParticipationModel();

    model.personalityQuizId = participation.personality_quiz_id;
    model.userId = participation.user_id;
    model.questions = participation.questions
      ? this.remapParticipationQuestions(participation.questions)
      : [];
    model.personas = participation.personas
      ? this.remapParticipationPersonas(participation.personas)
      : [];
    model.attempts = participation.attempts;

    return model;
  };

  public remapUserPersonalityQuizParticipations = (
    userParticipations: any
  ): PersonalityQuizUserParticipationsModel => {
    const model = new PersonalityQuizUserParticipationsModel();

    model.meta.pagination = this.remapMetaPagination(
      userParticipations.meta.pagination
    );
    model.data = userParticipations.data.map((value) =>
      this.remapPersonalityQuizParticipation(value)
    );

    return model;
  };

  private remapParticipationQuestions = (participationQuestions: any[]) => {
    return participationQuestions.map((value) => {
      const model = new PersonalityQuizParticipationQuestionModel();

      model.questionId = value.question_id;
      model.optionId = value.option_id;

      return model;
    });
  };

  private remapParticipationPersonas = (participationPersonas: any[]) => {
    return participationPersonas.map((value) => {
      const model = new PersonalityQuizPersonaPercentsModel();

      model.personaId = value.persona_id;
      model.percent = value.percent;

      return model;
    });
  };

  public remapPersonalityQuizParticipationBody = (
    body: PersonalityQuizParticipationBodyModel[]
  ) => {
    return body.map((value) => {
      return {
        question_id: value.questionId,
        option_id: value.optionId,
      };
    });
  };

  private remapMetaPagination = (pagination: any) => {
    const model = new MetaProperties();

    model.itemsPerPage = pagination.items_per_page;
    model.nextPageStartsAfter = pagination.next_page_starts_after;

    return model;
  };

  private remapBasicPersonalityQuiz = (basicClassicQuiz: any) => {
    const model = new PersonalityQuizBasicModel();

    model.id = basicClassicQuiz.id;
    model.title = basicClassicQuiz.title;
    model.alternativeTitle = basicClassicQuiz.alternative_title;
    model.description = basicClassicQuiz.description;
    model.images = basicClassicQuiz.images;
    model.participationCount = basicClassicQuiz.participation_count;
    model.status = basicClassicQuiz.status;
    model.language = basicClassicQuiz.language;
    model.authRequirement = basicClassicQuiz.auth_requirement;
    model.flags = basicClassicQuiz.flags;
    model.customFields = basicClassicQuiz.custom_fields;
    model.branding = basicClassicQuiz.branding
      ? this.remapBranding(basicClassicQuiz.branding)
      : null;
    model.labels = basicClassicQuiz.labels;
    model.time = basicClassicQuiz.time;
    model.related = basicClassicQuiz.related
      ? this.remapRelated(basicClassicQuiz.related)
      : [];
    model.maxAttempts = basicClassicQuiz.max_attempts;
    model.preferencesEnabled = basicClassicQuiz.preferences_enabled || false;
    model.context = basicClassicQuiz.context
      ? this.remapContext(basicClassicQuiz.context)
      : basicClassicQuiz.context;
    model.adContent = basicClassicQuiz.ad_content;
    model.createdAt = basicClassicQuiz.created_at;
    model.updatedAt = basicClassicQuiz.updated_at;

    return model;
  };

  private remapPersonaResults = (personaResults: any) => {
    const model = new PersonalityQuizPersonaResultsModel();

    model.personaId = personaResults.persona_id;
    model.title = personaResults.title;
    model.description = personaResults.description;
    model.images = personaResults.images;

    return model;
  };

  private remapQuestions = (question: any) => {
    const model = new PersonalityQuizQuestionModel();

    model.questionId = question.question_id;
    model.question = question.question;
    model.images = question.images;
    model.explanation = question.explanation;
    model.options = question.options.map((value) => this.remapOptions(value));

    return model;
  };

  private remapOptions = (question: any) => {
    const model = new PersonalityQuizOptionModel();

    model.optionId = question.option_id;
    model.option = question.option;
    model.images = question.images;
    model.preferencesMapping = question.preferences_mapping
      ? this.remapPreferencesMapping(question.preferences_mapping)
      : [];
    model.personaPoints = question.persona_points
      ? this.remapPersonaPoints(question.persona_points)
      : [];

    return model;
  };

  private remapPreferencesMapping = (response: any[]) => {
    return response.map((r) => {
      const model = new PreferenceMappingModel();

      model.preferenceId = r.preference_id;

      return model;
    });
  };

  private remapPersonaPoints = (personaPoints: any[]) => {
    return personaPoints.map((r) => {
      const model = new PersonalityQuizPersonaPointsModel();

      model.personaId = r.persona_id;
      model.points = r.points;

      return model;
    });
  };

  private remapContext = (data: any) => {
    const model = new MiniGamesContext();

    model.content = data.content;
    model.tags = data.tags
      ? data.tags.map((value) => {
          const newTag = new MiniGamesContextTag();

          newTag.id = value.id;
          newTag.type = value.type;
          newTag.source = value.source;

          return newTag;
        })
      : null;
    model.campaign = data.campaign;

    return model;
  };

  private remapRelated = (response: any[]) => {
    return response.map((r) => {
      const model = new RelatedEntityRelationship();

      model.entityId = r.entity_id;
      model.entityType = r.entity_type;
      model.entityRelationship = r.entity_relationship;

      return model;
    });
  };
}
